import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { PlanIntervals } from 'src/constants/planData'
import TabSelector from 'src/components/common/TabSelector'
import styled from 'styled-components'
import { useMultipleChannelSelectors } from 'src/modules/hooks/useChannelSelector'
import { buildPlan, getMonthlyPrice } from 'src/modules/utils/plans'
import { useSelector } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { fireUIInteractionEvent, fireUIRenderEvent } from 'src/modules/analytics/v3'
import { selectAnalyticsReady } from 'src/modules/reducers/analytics'
import { useMarketCtaTexts } from 'src/modules/hooks/useCtaText'
import { colors, maxWidth, mediaQuery, spacing } from 'src/constants/marketing-ds'
import PlanCardWithImage from 'src/components/common/PlanCardWithImage'
import { SectionContainer, SectionHeader } from '../SharedComponents'

/**
 * PlanCardsWithTabs Component Documentation
 *
 * This component displays plan cards with tabbed navigation for different billing intervals
 * (monthly, quarterly, annual). It's specifically designed for the Canadian market.
 *
 * Example:
 *     <CanadaTemplateDefault
 *       // ... other props
 *       hasPlanCardsTabs={true}
 *       planCardsConfig={see example below}
 *     />
 *   )
 *
 * Key Points:
 * 1. hasPlanCardsTabs (boolean) must be true to enable the component
 * 2. planCardsConfig (object) contains the configuration object
 *    - plans: [canadaPlanData.premium, canadaPlanData.soccer] // required
 *           - or use canadaPlanSet for set combinations
 *    - activeTab?: PlanIntervals.monthly // defaults to quarterly if not specified or displayMode is 'plans' and not specified
 *         - If displayMode is 'plans' and activeTab is not specified, it will default to the first plan's title
 *         - If displayMode is 'plans', activeTab can be the plan title
 *    - sharedData?: { // shared data for all plans
 *    - overrides?: {
 *        all: { // overrides for all plans for all intervals
 *          promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/ca_card_soccer-arsenal.jpg?w=451&ch=width&auto=format,compress',
 *        },
 *        premium: { // plan name
 *          all: { // overrides for all intervals for this plan
 *            promoImg: 'https://assets-imgx.fubo.tv/marketing/lp/images/card/ca_card_soccer-arsenal.jpg?w=451&ch=width&auto=format,compress',
 *          },
 *          annual: null, // Set to null to hide this interval for the plan
 *        },
 *        sports: {
 *          annual: {price: 100, discountedPrice: 90, savingsNote: '10% off'}, // Interval specific override
 *        },
 *      }
 *    - displayMode?: 'intervals' | 'plans' // controls tab behavior (default: 'intervals')
 *
 * DISPLAY MODES:
 *
 * 1. INTERVALS MODE (displayMode: 'intervals' - DEFAULT):
 *    - Tabs show billing intervals (Monthly, Quarterly, Annual)
 *    - Displays all plans side-by-side for the selected interval
 *    - Example: When "Monthly" tab is selected, shows Premium Monthly, Soccer Monthly, etc.
 *
 *    Usage:
 *    ```javascript
 *    <PlanCardsWithTabs
 *      plans={[canadaPlanData.premium, canadaPlanData.soccer]}
 *      displayMode="intervals"
 *      tabs={[PlanIntervals.monthly, PlanIntervals.annual]}
 *      activeTab={PlanIntervals.monthly}
 *    />
 *    ```
 *
 * 2. PLANS MODE (displayMode: 'plans'):
 *    - Tabs show plan names (Premium, Soccer, MLB.TV, etc.)
 *    - Displays all intervals for the selected plan side-by-side
 *    - Example: When "Premium" tab is selected, shows Premium Monthly, Premium Annual, etc.
 *
 *    Usage:
 *    ```javascript
 *    <PlanCardsWithTabs
 *      plans={[canadaPlanData.premium, canadaPlanData.soccer]}
 *      displayMode="plans"
 *      tabs={[PlanIntervals.monthly, PlanIntervals.annual]}
 *      activeTab="Premium" // This now refers to the plan name, not interval
 *    />
 *    ```
 *
 * PROPS:
 * - displayMode: 'intervals' | 'plans' - Controls tab behavior
 * - plans: Array of plan objects - Required
 * - tabs: Array of intervals to show (defaults to all PlanIntervals)
 * - activeTab: String - For intervals mode: interval name; For plans mode: plan title
 * - market: String - Market identifier (e.g., 'canada')
 * - title: String - Section title
 * - subtitle: String - Section subtitle
 * - sharedData: Object - Data shared across all plans
 * - overrides: Object - Plan-specific overrides
 * - hideTabs: Boolean - Hide tab selector
 * - ctaText: String - Custom CTA text
 * - tabSavings: Object - Savings text per tab (e.g., { quarterly: "Save 15%", annual: "Save 25%" })
 */

export const TABS = Object.keys(PlanIntervals)

export const PlanCardsWithTabs = ({
  lang = 'en',
  plans = [],
  tabs = TABS,
  activeTab,
  market,
  title,
  subtitle,
  sharedData = {},
  overrides = {},
  hideTabs = false,
  ctaText,
  displayMode = 'intervals', //intervals or plans
  tabSavings = {},
}) => {
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const [tab, setTab] = useState(() => {
    if (displayMode === 'plans') {
      return activeTab || plans[0]?.title || TABS[0]
    }
    return activeTab || TABS[0]
  })
  const [bannerHeight, setBannerHeight] = useState(0)
  const [isMounted, setIsMounted] = useState(false)
  const [allPlansBuilt, setAllPlansBuilt] = useState({})
  const hasBuiltPlans = useRef(false)
  const isEventSent = useRef(false)
  const analyticsReady = useSelector(selectAnalyticsReady)
  const defaultMarketCtaTexts = useMarketCtaTexts({ lang })
  const sectionContentRef = useRef(null)

  // Memoize tabs to prevent recalculation
  const planTabs = useMemo(() => plans.map((plan) => plan.title), [plans])
  // Use tabs prop if provided, otherwise use TABS constant (stable reference)
  const intervalTabs = tabs ?? TABS

  // Memoize channel types to prevent unnecessary recalculations
  const channelTypes = useMemo(() => {
    return plans.map((plan) => plan?.channelId)
  }, [plans])

  // Memoize channel list to prevent unnecessary recalculations
  const channelList = useMultipleChannelSelectors(channelTypes)

  // Check if any current plans have banners (text or image)
  const hasBanners = useMemo(() => {
    return plans.some((plan) => {
      // Check if any interval of this plan has a banner
      if (displayMode === 'intervals') {
        const builtPlan = buildPlan(market, plan, tab, overrides?.[plan.plan])
        return builtPlan?.banner || builtPlan?.bannerImage
      } else {
        // For plans mode, check all intervals
        return intervalTabs.some((interval) => {
          const builtPlan = buildPlan(market, plan, interval, overrides?.[plan.plan])
          return builtPlan?.banner || builtPlan?.bannerImage
        })
      }
    })
  }, [plans, market, tab, overrides, displayMode, intervalTabs])

  // Get current plans from pre-built plans (simple lookup, no recalculation)
  const currentPlans = allPlansBuilt[tab] || []

  // Memoize tab click handler
  const handleTabClick = useCallback(
    (selected) => {
      setTab(selected)
      fireUIInteractionEvent(
        {
          element: selected,
          component: 'plan_tab_clicked',
        },
        stateForProtobuf
      )
    },
    [stateForProtobuf]
  )

  // Set mounted flag after hydration
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Build all plans once after mount to prevent hydration mismatch from buildPlan
  useEffect(() => {
    if (!isMounted) return // Wait for mount
    if (hasBuiltPlans.current) return // Only build once

    // Build all plans once after mount
    const plansByTab = {}

    if (displayMode === 'intervals') {
      // For intervals mode, create plans for each interval
      intervalTabs.forEach((interval) => {
        plansByTab[interval] = plans
          .map((plan) => buildPlan(market, plan, interval, overrides?.[plan.plan]))
          .filter(Boolean)
          .map((plan) => ({
            ...plan,
            _stableId: `${plan.plan}-${interval}`,
          }))
      })
    } else {
      // For plans mode, create plans for each plan showing all intervals
      plans.forEach((planConfig) => {
        plansByTab[planConfig.title] = intervalTabs
          .map((interval) => buildPlan(market, planConfig, interval, overrides?.[planConfig.plan]))
          .filter(Boolean)
          .map((plan, index) => ({
            ...plan,
            _stableId: `${plan.plan}-${plan.interval || intervalTabs[index]}`,
          }))
      })
    }

    setAllPlansBuilt(plansByTab)
    hasBuiltPlans.current = true
  }, [isMounted, plans, market, overrides, displayMode, intervalTabs])

  useEffect(() => {
    if (isMounted && activeTab !== tab) {
      if (displayMode === 'plans') {
        setTab(activeTab || plans[0]?.title)
      } else {
        setTab(activeTab || TABS[0])
      }
    }
  }, [activeTab, isMounted, displayMode])

  // Measure banner heights after render
  useEffect(() => {
    if (!hasBanners) {
      setBannerHeight(0)
      return
    }

    const measureBannerHeights = () => {
      if (!sectionContentRef.current) return

      const banners = sectionContentRef.current.querySelectorAll('.planCard__banner')
      let maxHeight = 0

      banners.forEach((banner) => {
        const height = banner.getBoundingClientRect().height
        maxHeight = Math.max(maxHeight, height)
      })

      setBannerHeight(maxHeight)
    }

    const timeoutId = setTimeout(measureBannerHeights, 100)
    window.addEventListener('resize', measureBannerHeights)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', measureBannerHeights)
    }
  }, [hasBanners, currentPlans])

  useEffect(() => {
    if (!isEventSent.current && plans.length > 0 && analyticsReady) {
      fireUIRenderEvent(
        {
          element: plans.map((p) => p.plan).join(', '),
          component: 'plan_cards',
        },
        stateForProtobuf
      )
      isEventSent.current = true
    }
  }, [plans, analyticsReady, stateForProtobuf])

  if (plans.length === 0 || currentPlans.length === 0) {
    return null
  }

  return (
    <SectionContainer backgroundColor={colors.gradients.slate.light}>
      <StyledBundleSection id="select-plan">
        <SectionHeader title={title} subtitle={subtitle} variant="light" />
        {!hideTabs && (
          <div className="section-tabs">
            <TabSelector
              tabs={displayMode === 'intervals' ? intervalTabs : planTabs}
              activeTab={tab}
              handleTabClick={handleTabClick}
              tabSavings={tabSavings}
            />
          </div>
        )}
        <StyledSectionContent ref={sectionContentRef} bannerHeight={bannerHeight}>
          {currentPlans?.map(
            (plan, index) =>
              plan?.plan && (
                <PlanCardWithImage
                  lang={lang}
                  key={index}
                  promoImg={plan.promoImg}
                  plan={plan.plan + ' ' + plan.interval}
                  title={
                    <>
                      {plan.title} <span className="capitalize">{plan.interval}</span>
                    </>
                  }
                  subtitle={plan.subtitle}
                  price={getMonthlyPrice(plan.price, plan.interval)}
                  discountedPrice={getMonthlyPrice(plan.discountedPrice, plan.interval)}
                  savingsNote={plan.savingsNote}
                  listItems={plan.listItems({
                    teams: plan.teams,
                    rsn: plan.rsn,
                    channels: channelList[plan.channelId],
                    ...sharedData,
                  })}
                  offerCode={plan.offerCode}
                  asterisk={plan.asterisk || plan?.footnote}
                  networkLogos={plan.networkLogos}
                  networkLogosTop={plan.networkLogosTop}
                  ctaText={ctaText || plan?.ctaText || defaultMarketCtaTexts[market]}
                  cardClassName={
                    plan.plan +
                    (plan.banner || plan.bannerImage ? ' planCard-container-with-banner' : '')
                  }
                  banner={plan.banner}
                  bannerImage={plan.bannerImage}
                  backgroundColor={plan.backgroundColor}
                  imageOverlay={
                    plan.imageOverlay ||
                    'linear-gradient(186deg, rgba(4, 9, 17, 0) 15.04%, rgb(2, 9, 17) 65.16%)'
                  }
                  footnote={
                    typeof plan?.footnote === 'function'
                      ? plan.footnote({
                          price: plan.price,
                          discountedPrice: plan.discountedPrice,
                          rsnName: plan.rsnName,
                        })
                      : plan?.footnote
                  }
                />
              )
          )}
        </StyledSectionContent>
      </StyledBundleSection>
    </SectionContainer>
  )
}

const StyledBundleSection = styled.section`
  color: #fff;
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${colors.gradients.slate.light};
  gap: ${spacing.md};
  padding-top: ${spacing.xl};
  padding-bottom: ${spacing.xl};
  ${mediaQuery.tablet} {
    padding-top: ${spacing.xxxl};
    padding-bottom: ${spacing.xxxl};
    gap: ${spacing.xl};
  }
  ${mediaQuery.desktop} {
    gap: ${spacing.xxl};
  }

  .section-tabs {
    width: 100%;
    max-width: 600px;

    .plan-tabs {
      overflow: hidden;
      margin: 0 auto 30px;
      display: flex;
      width: 100%;
      max-width: 500px;
      align-items: flex-end;
      > * {
        flex: 1 0 0;
      }
      .tab {
        position: relative;
      }
      .savings {
        text-align: center;
        margin-bottom: 10px;
        font-size: 14px;
        font-weight: 500;
      }
      .tab:first-child {
        button {
          border-radius: 10px 0 0 10px;
        }
      }
      .tab:last-child {
        button {
          border-radius: 0 10px 10px 0;
        }
      }
      button {
        width: 100%;
        ${'' /* Reset Button CSS */}
        border: none;
        margin: 0;
        padding: 15px 0;
        overflow: visible;
        background: transparent;
        color: inherit;
        font: inherit;
        line-height: normal;
        -webkit-font-smoothing: inherit;
        -moz-osx-font-smoothing: inherit;
        -webkit-appearance: none;
        &::-moz-focus-inner {
          border: 0;
          padding: 0;
        }

        cursor: pointer;
        background: #ffffff;
        color: #000;

        &:hover {
          background: #f0f0f0;
        }

        &.active {
          background: #000;
          color: #fff;
        }

        .tab-content {
          font-size: 18px;
          font-weight: 500;
          text-align: center;
          -webkit-letter-spacing: -0.17px;
          -moz-letter-spacing: -0.17px;
          -ms-letter-spacing: -0.17px;
          letter-spacing: -0.17px;
          line-height: 20px;
          text-transform: uppercase;
          @media (max-width: 600px) {
            font-size: 17px;
          }
        }
      }
    }
  }

  .capitalize {
    text-transform: capitalize;
  }
`

const StyledSectionContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: ${maxWidth.desktop};
  gap: ${spacing.lg};
  flex-direction: column;
  min-height: 600px;
  ${mediaQuery.desktop} {
    flex-direction: row;
    gap: ${spacing.xl};
    align-items: stretch;
    min-height: 500px;
    gap: ${spacing.md};
  }

  /* Dynamic top padding based on banner height */
  ${(props) =>
    props.bannerHeight &&
    props.bannerHeight > 0 &&
    `
    padding-top: ${props.bannerHeight}px;
    .planCard-container-with-banner:not(:first-child) {
      margin-top: ${props.bannerHeight}px;
      ${mediaQuery.desktop} {
        margin-top: 0;
      }
    }
  `}
`
